import { podPhaseView } from "./pod-phase.js";
import { appPodContainerSchema, podProblemEnum } from "./app-pods.js";
import { z } from "zod";
//#region src/cp-pods.ts
/**
* Control-plane pod introspection wire types —
* `GET /v1/admin/control-plane/pods` (site-admin-api observability module, #1251).
*
* ## ⛔ WHY THERE IS NO CP POD-**LOG** SCHEMA IN THIS FILE
*
* The log read binds `appPodLogQuerySchema` and returns `AppPodLogResponse`
* (`./app-pod-log.js`) UNCHANGED. The question is identical — one container, one
* shot, bounded, `previous`, `tailLines` — and the two bound-and-truncation
* rulings (`truncated: true` means "ask for more", a 413 means "ask for fewer")
* are the same rulings. A second copy under a `cp` name would be a fork with a
* different name, and the first producer change would make them disagree.
*
* ## ⛔ THE NAMESPACE IS NEVER CLIENT-SUPPLIED
*
* Neither schema below carries a `namespace`, and that is a refusal rather than
* an omission: the server confines every read to the namespace the site-admin-api
* pod is itself running in (`CP_NAMESPACE`, from the downward API), and the k8s
* Role is namespaced to that same namespace, so a foreign namespace is refused
* twice. `.strict()` makes the key a 400 rather than a silently-ignored input.
*/
/**
* DNS-1123 SUBDOMAIN — the k8s pod-name grammar (≤253, dots allowed).
*
* ⛔ Validated at the `@Param` boundary, unlike `AppPodLogController`'s `podName`
* (`site-admin-api/src/modules/observability/app-pod-log.controller.ts:95-100`),
* which deliberately applies NO pipe because its scope check needs the
* DB-resolved slug. There is no such coupling here: the namespace is the whole
* confinement, so the grammar can and must be enforced at the door.
*
* ⚠️ Length is bounded BEFORE the pattern is applied, per the repo's ReDoS rule.
*/
const cpPodNameSchema = z.string().min(1).max(253).regex(/^[a-z0-9]([-a-z0-9.]{0,251}[a-z0-9])?$/);
/** One control-plane pod. */
const cpPodSummarySchema = z.object({
	name: z.string().min(1),
	/**
	* k8s pod phase — `Pending` | `Running` | `Succeeded` | `Failed` | `Unknown`.
	*
	* ⛔ **ENUMERATED, WITH A DEGRADING FALLBACK (#1994).** The docblock enumerated five values
	* over a `z.string().min(1)`, so `'Nonsense'` parsed clean while `''` was the only rejection.
	* {@link podPhaseView} maps an unrecognised value to `'Unknown'` and still rejects a missing
	* key, a `null` and a non-string. ⚠️ `''` now normalises to `'Unknown'` rather than failing —
	* an empty phase was never a real kubelet value, and degrading it matches every other
	* unrecognised string rather than singling it out.
	*/
	phase: podPhaseView,
	/**
	* `app.kubernetes.io/name`, the label every CP workload template sets.
	* ⛔ `null` when the pod carries no such label — the console GROUPS on this,
	* and a fabricated group name would put an unlabelled pod under a service
	* that does not own it.
	*/
	component: z.string().nullable(),
	/**
	* Whole seconds since `status.startTime`. ⛔ `null`, not `0`, when k8s
	* reported no start time — `0` renders a week-old pod as brand new. Same
	* ruling as `appPodSummarySchema.ageSeconds`.
	*/
	ageSeconds: z.number().int().nonnegative().nullable(),
	/** Sum of `restartCount` across every container. */
	restarts: z.number().int().nonnegative(),
	/** `spec.nodeName`. `null` is DIAGNOSTIC: an unscheduled pod has no node yet. */
	nodeName: z.string().nullable(),
	containers: z.array(appPodContainerSchema),
	/**
	* Derived by the same `derivePodProblems` the app surface uses
	* (`site-admin-api/src/modules/observability/pod-problems.ts:116`).
	* `[]` means checked-and-nothing-wrong; it never means not-evaluated.
	*/
	problems: z.array(podProblemEnum)
}).strict();
/**
* Hard ceiling on pods assembled for one response.
*
* ⚠️ Not a page size — there is no paging on this route and there deliberately
* is none: the CP namespace is a fixed, small workload set (5 services + infra),
* so a cursor would be machinery over a list that fits on one screen. The cap
* exists so a pathological namespace cannot make this an unbounded read, and
* `truncated` states when it bound.
*/
const MAX_CP_PODS = 200;
const cpPodsResponseSchema = z.object({
	/** The namespace the server read. Echoed so the console never has to guess it. */
	namespace: z.string().min(1),
	/** ⛔ `[]` means the namespace has no pods — never a rendering of a failure. */
	pods: z.array(cpPodSummarySchema),
	/** TRUE ⇒ more than {@link MAX_CP_PODS} pods exist and the tail was dropped. */
	truncated: z.boolean()
}).strict();
//#endregion
export { MAX_CP_PODS, cpPodNameSchema, cpPodSummarySchema, cpPodsResponseSchema };

//# sourceMappingURL=cp-pods.js.map